import React, { FC } from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ProductionOverviewCountMode } from 'models/production-overview';
import OverviewGroupRow, { OverviewCellClickHandler } from './overview-group-row.component';
import {
    OverviewCalendarGroup,
    getDayOfMonthLabel,
    getTodayKey,
    getVisibleMaxCount,
    getWeekdayLabel,
    isMonday,
    isWeekendDay,
} from './production-overview-utils';

type OverviewCalendarTableProps = {
    groups: OverviewCalendarGroup[];
    days: string[];
    countMode: ProductionOverviewCountMode;
    /** The 30-day view draws smaller text in narrower columns. */
    isCompact: boolean;
    onCellClick: OverviewCellClickHandler;
};

const OverviewCalendarTable: FC<OverviewCalendarTableProps> = ({
    groups,
    days,
    countMode,
    isCompact,
    onCellClick,
}: OverviewCalendarTableProps) => {
    const { t: translate } = useTranslation(['production-overview']);
    const todayKey = getTodayKey();
    const heatMaxCount = getVisibleMaxCount(groups, days, countMode);

    return (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                    <TableRow>
                        <TableCell
                            sx={{
                                minWidth: isCompact ? 150 : 180,
                                position: 'sticky',
                                left: 0,
                                backgroundColor: '#fff',
                                zIndex: 2,
                            }}
                        >
                            {translate('production-overview:CALENDAR_FIRST_COLUMN')}
                        </TableCell>
                        {days.map((dayKey) => {
                            const isToday = dayKey === todayKey;
                            return (
                                <TableCell
                                    key={dayKey}
                                    align="center"
                                    sx={{
                                        backgroundColor: isWeekendDay(dayKey) ? '#f5f5f5' : '#fff',
                                        minWidth: isCompact ? 36 : 64,
                                        whiteSpace: 'nowrap',
                                        borderLeft: isMonday(dayKey) ? '2px solid #ccc' : undefined,
                                        borderBottom: isToday ? '3px solid #1e88e5' : undefined,
                                        paddingX: isCompact ? 0.25 : 0.5,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        component="div"
                                        color="text.secondary"
                                        fontSize={isCompact ? '0.6rem' : undefined}
                                    >
                                        {getWeekdayLabel(dayKey)}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        component="div"
                                        fontWeight={isToday ? 700 : 500}
                                        fontSize={isCompact ? '0.7rem' : undefined}
                                    >
                                        {getDayOfMonthLabel(dayKey)}
                                    </Typography>
                                </TableCell>
                            );
                        })}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {groups.map((group) => (
                        <OverviewGroupRow
                            key={group.groupKey}
                            group={group}
                            days={days}
                            countMode={countMode}
                            heatMaxCount={heatMaxCount}
                            isCompact={isCompact}
                            onCellClick={onCellClick}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default OverviewCalendarTable;
